import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable para manejar la funcionalidad de pantalla completa usando la Fullscreen API
 */
export function useFullscreen(targetElement?: HTMLElement | null) {
  const isFullscreen = ref(false);
  const element = ref<HTMLElement | null>(targetElement || null);

  // Detectar si el navegador soporta la Fullscreen API
  const isSupported = (): boolean => {
    return !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  };

  // Obtener el elemento actual en pantalla completa
  const getFullscreenElement = (): Element | null => {
    return (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  };

  // Solicitar pantalla completa
  const enterFullscreen = async (): Promise<void> => {
    if (!isSupported()) {
      console.warn('La API de pantalla completa no está soportada en este navegador');
      return;
    }

    const target = element.value || document.documentElement;

    try {
      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else if ((target as any).webkitRequestFullscreen) {
        await (target as any).webkitRequestFullscreen();
      } else if ((target as any).mozRequestFullScreen) {
        await (target as any).mozRequestFullScreen();
      } else if ((target as any).msRequestFullscreen) {
        await (target as any).msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error al entrar en pantalla completa:', error);
      throw error;
    }
  };

  // Salir de pantalla completa
  const exitFullscreen = async (): Promise<void> => {
    if (!isSupported()) {
      return;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Error al salir de pantalla completa:', error);
      throw error;
    }
  };

  // Alternar pantalla completa
  const toggleFullscreen = async (): Promise<void> => {
    if (isFullscreen.value) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  };

  // Actualizar el estado de pantalla completa
  const updateFullscreenState = (): void => {
    isFullscreen.value = !!getFullscreenElement();
  };

  // Manejar eventos de cambio de pantalla completa
  const handleFullscreenChange = (): void => {
    updateFullscreenState();
  };

  // Establecer el elemento objetivo
  const setTarget = (target: HTMLElement | null): void => {
    element.value = target;
  };

  // Inicializar listeners
  onMounted(() => {
    updateFullscreenState();

    // Escuchar eventos de cambio de pantalla completa (con prefijos para compatibilidad)
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
  });

  // Limpiar listeners
  onUnmounted(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
  });

  // Crear una referencia reactiva para el soporte
  const supported = ref(isSupported());

  return {
    isFullscreen,
    isSupported: supported.value,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    setTarget
  };
}

